import { faqCategories as fallbackFaqCategories } from "@/mocks/faqData";

export type FaqItem = {
  id: number;
  faqCategoryId: number | null;
  question: string;
  answer: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type FaqItemPayload = {
  FaqCategoryId: number;
  Question: string;
  Answer: string;
  IsActive: boolean;
  SortOrder: number;
};

export type FaqCategory = {
  id: number;
  name: string;
  icon: string;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type FaqCategoryPayload = {
  Name: string;
  Icon: string;
  Description: string;
  IsActive: boolean;
  SortOrder: number;
};

export type FaqCategoryOption = {
  id: number;
  name: string;
};

export type PublicFaqGroup = {
  faqCategoryId: number;
  category: string;
  icon: string;
  items: { id: number; question: string; answer: string }[];
};

type ApiFaqItem = {
  FaqItemId: string | number;
  FaqCategoryId: string | number;
  Question: string;
  Answer: string;
  IsActive?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiFaqCategory = {
  FaqCategoryId: string | number;
  Name: string;
  Icon?: string;
  Description?: string | null;
  IsActive?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiPublicFaqGroup = {
  FaqCategoryId: string | number;
  Category: string;
  Icon: string;
  Items: { FaqItemId: string | number; Question: string; Answer: string }[];
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
};

const adminCsrfKey = "fulling_admin_csrf";

function getAdminCsrfToken() {
  return sessionStorage.getItem(adminCsrfKey) || "";
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料處理失敗，請稍後再試。");
  }
  return data as T;
}

function adminHeaders(json = true) {
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(getAdminCsrfToken() ? { "X-CSRF-Token": getAdminCsrfToken() } : {}),
  };
}

function normalizeItem(item: ApiFaqItem): FaqItem {
  return {
    id: Number(item.FaqItemId),
    faqCategoryId: item.FaqCategoryId ? Number(item.FaqCategoryId) : null,
    question: item.Question || "",
    answer: item.Answer || "",
    isActive: item.IsActive !== false,
    sortOrder: Number(item.SortOrder || 100),
    createdAt: item.CreatedAt || null,
    updatedAt: item.UpdatedAt || null,
  };
}

function normalizeCategory(category: ApiFaqCategory): FaqCategory {
  return {
    id: Number(category.FaqCategoryId),
    name: category.Name || "",
    icon: category.Icon || "ri-question-line",
    description: category.Description || "",
    isActive: category.IsActive !== false,
    sortOrder: Number(category.SortOrder || 100),
    createdAt: category.CreatedAt || null,
    updatedAt: category.UpdatedAt || null,
  };
}

function normalizePublicGroup(group: ApiPublicFaqGroup): PublicFaqGroup {
  return {
    faqCategoryId: Number(group.FaqCategoryId),
    category: group.Category || "",
    icon: group.Icon || "ri-question-line",
    items: (group.Items || []).map((item) => ({
      id: Number(item.FaqItemId),
      question: item.Question || "",
      answer: item.Answer || "",
    })),
  };
}

function fallbackPublicGroups(): PublicFaqGroup[] {
  return fallbackFaqCategories.map((category, categoryIndex) => ({
    faqCategoryId: categoryIndex + 1,
    category: category.category,
    icon: category.icon,
    items: category.items.map((item, itemIndex) => ({
      id: categoryIndex * 100 + itemIndex + 1,
      question: item.question,
      answer: item.answer,
    })),
  }));
}

export async function fetchPublicFaq(): Promise<PublicFaqGroup[]> {
  try {
    const data = await fetch("/api/faq", {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; Categories: ApiPublicFaqGroup[] }>(response));

    const groups = (data.Categories || []).map(normalizePublicGroup);
    return groups.length ? groups : fallbackPublicGroups();
  } catch {
    return fallbackPublicGroups();
  }
}

export async function listAdminFaqCategories() {
  const data = await fetch("/api/admin/faq-categories", {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Categories: ApiFaqCategory[] }>(response));
  return data.Categories.map(normalizeCategory);
}

export async function saveAdminFaqCategory(categoryId: number | null, payload: FaqCategoryPayload) {
  const path = categoryId ? `/api/admin/faq-categories/${categoryId}` : "/api/admin/faq-categories";
  const method = categoryId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Category: ApiFaqCategory }>(response));
  return normalizeCategory(data.Category);
}

export async function deleteAdminFaqCategory(categoryId: number) {
  return fetch(`/api/admin/faq-categories/${categoryId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function listAdminFaqItems(params: { Status?: string; FaqCategoryId?: string; Keyword?: string; Page?: number; PerPage?: number }) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.FaqCategoryId) query.set("FaqCategoryId", params.FaqCategoryId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const data = await fetch(`/api/admin/faq-items${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{
    ok: boolean;
    Items: ApiFaqItem[];
    Counts?: { Active: number; Inactive: number };
    Categories?: ApiFaqCategory[];
    Pagination: { Page: number; PerPage: number; Total: number; TotalPages: number };
  }>(response));

  return {
    ...data,
    Items: data.Items.map(normalizeItem),
    Categories: (data.Categories || []).map((category) => ({ id: Number(category.FaqCategoryId), name: category.Name })) as FaqCategoryOption[],
  };
}

export async function getAdminFaqItem(itemId: string | number) {
  const data = await fetch(`/api/admin/faq-items/${itemId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Item: ApiFaqItem }>(response));
  return normalizeItem(data.Item);
}

export async function saveAdminFaqItem(itemId: string | number | null, payload: FaqItemPayload) {
  const path = itemId ? `/api/admin/faq-items/${itemId}` : "/api/admin/faq-items";
  const method = itemId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Item: ApiFaqItem }>(response));
  return normalizeItem(data.Item);
}

export async function deleteAdminFaqItem(itemId: string | number) {
  return fetch(`/api/admin/faq-items/${itemId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}
